import { useState, useMemo, useEffect } from "react";
import { ArrowRight, ArrowLeft, BookOpen, Sparkles, Hammer, MessageSquareHeart, CheckCircle2 } from "lucide-react";
import LessonVideo from "@/components/LessonVideo";
import MicButton from "@/components/accessibility/MicButton";
import ReadAloudButton from "@/components/accessibility/ReadAloudButton";

/**
 * Generic SessionFlow — renders a Middle School session from a structured schema.
 * Stages: Learn → Engage (MCQs) → Activity (step-by-step) → Build → Reflect.
 *
 * Props:
 *  - session: full session schema (see middle_school_v2_seed.py)
 *  - savedResponses: { stage, engageIdx, activityIdx, engage, activity, build, reflect }
 *  - onChange(state): debounced auto-save
 *  - onContextChange(prompts): set LemonBot suggested prompts based on engage answers
 *  - onReadyToSubmit(submission): called when Reflect is filled and student clicks "Finish Session"
 *  - submitting: bool
 */

const STAGES = ["learn", "engage", "activity", "build", "reflect"];

function SessionFlow({ session, savedResponses, onChange, onContextChange, onReadyToSubmit, submitting }) {
  const engageQs = session.engage || [];
  const activitySteps = session.activity || [];
  const buildFields = (session.build && session.build.fields) || [];
  const reflect = session.reflect || null;

  const [stage, setStage] = useState(savedResponses?.stage || "learn");
  const [engageIdx, setEngageIdx] = useState(savedResponses?.engageIdx ?? 0);
  const [activityIdx, setActivityIdx] = useState(savedResponses?.activityIdx ?? 0);
  const [engage, setEngage] = useState(savedResponses?.engage || {});
  const [activity, setActivity] = useState(savedResponses?.activity || {});
  const [build, setBuild] = useState(savedResponses?.build || {});
  const [reflectAnswer, setReflectAnswer] = useState(savedResponses?.reflect || {});

  // Master step indicator
  const TOTAL = 1 /* learn */ + Math.max(engageQs.length, 1) + activitySteps.length + 1 /* build */ + 1 /* reflect */;
  let current = 1;
  if (stage === "learn") current = 1;
  else if (stage === "engage") current = 1 + (engageQs.length ? engageIdx + 1 : 1);
  else if (stage === "activity") current = 1 + Math.max(engageQs.length, 1) + activityIdx + 1;
  else if (stage === "build") current = 1 + Math.max(engageQs.length, 1) + activitySteps.length + 1;
  else if (stage === "reflect") current = TOTAL;

  // Persist
  useEffect(() => {
    onChange?.({ stage, engageIdx, activityIdx, engage, activity, build, reflect: reflectAnswer });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, engageIdx, activityIdx, engage, activity, build, reflectAnswer]);

  // Contextual LemonBot prompts — pick from session.engage_to_prompts based on engage answers
  const baselinePrompts = useMemo(() => session.lemonbot_prompts || [], [session.lemonbot_prompts]);
  useEffect(() => {
    const map = session.engage_to_prompts || {};
    let chosen = null;
    for (const qid of Object.keys(map)) {
      const ans = engage[qid];
      if (ans && map[qid][ans]) {
        chosen = map[qid][ans];
        break;
      }
    }
    onContextChange?.(chosen || baselinePrompts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engage, baselinePrompts]);

  // ===== LEARN =====
  if (stage === "learn") {
    return (
      <Card testId="sf-learn">
        <Master current={current} total={TOTAL} />
        <Heading icon={<BookOpen className="w-5 h-5 text-blue-500" />} label="Learn" />
        <h3 className="text-xl font-bold text-gray-900 mt-2 mb-3" data-testid="sf-learn-title">
          {session.title}
        </h3>
        {(session.learn?.video_url || session.learn?.video_placeholder) && (
          <div className="mb-4">
            <LessonVideo
              url={session.learn?.video_url}
              title={session.title}
              placeholder={session.learn?.video_placeholder}
            />
          </div>
        )}
        <div className="whitespace-pre-wrap text-gray-700 leading-relaxed text-base" data-testid="sf-learn-content">
          {session.learn?.content || ""}
        </div>
        {session.learn?.content && (
          <div className="mt-2 flex justify-end">
            <ReadAloudButton
              text={`${session.title}. ${session.learn.content}`}
              testId="sf-learn-read-aloud"
              inline
            />
          </div>
        )}
        <NextRow
          onNext={() => setStage(engageQs.length > 0 ? "engage" : "activity")}
          label={engageQs.length > 0 ? "Let's Explore" : "Start Activity"}
          testId="sf-learn-next-btn"
        />
      </Card>
    );
  }

  // ===== ENGAGE (MCQ scenarios) =====
  if (stage === "engage") {
    if (engageQs.length === 0) {
      setStage("activity");
      return null;
    }
    const q = engageQs[engageIdx];
    const value = engage[q.id] || "";
    const canAdvance = !!value;
    return (
      <Card testId="sf-engage">
        <Master current={current} total={TOTAL} />
        <Heading icon={<Sparkles className="w-5 h-5 text-purple-500" />} label="Engage" />
        <Pill
          color="purple"
          text={`Question ${engageIdx + 1} of ${engageQs.length}`}
          testId="sf-engage-position"
        />
        <h3 className="text-lg font-bold text-gray-900 mt-3 mb-1">{q.label || q.question}</h3>
        {q.purpose && <p className="text-gray-600 mb-4 text-sm">{q.purpose}</p>}
        <div className="space-y-3" role="radiogroup">
          {q.options.map((opt) => {
            const selected = value === opt.value;
            return (
              <label
                key={opt.value}
                className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  selected
                    ? "bg-purple-50 border-purple-500 shadow-sm"
                    : "bg-white border-gray-200 hover:border-purple-300"
                }`}
                data-testid={`sf-engage-${q.id}-${opt.value}`}
              >
                <input
                  type="radio"
                  name={q.id}
                  value={opt.value}
                  checked={selected}
                  onChange={() => setEngage((p) => ({ ...p, [q.id]: opt.value }))}
                  className="mt-1 accent-purple-500"
                />
                <span className="text-gray-800 font-medium">{opt.label}</span>
              </label>
            );
          })}
        </div>
        <NavRow
          onBack={engageIdx > 0 ? () => setEngageIdx(engageIdx - 1) : () => setStage("learn")}
          backLabel={engageIdx > 0 ? "Back" : "Back to Learn"}
          onNext={() => {
            if (engageIdx < engageQs.length - 1) setEngageIdx(engageIdx + 1);
            else setStage("activity");
          }}
          nextDisabled={!canAdvance}
          nextLabel={engageIdx < engageQs.length - 1 ? "Next" : "Start Activity"}
          testIdPrefix="sf-engage"
        />
      </Card>
    );
  }

  // ===== ACTIVITY (step-by-step) =====
  if (stage === "activity") {
    if (activitySteps.length === 0) {
      setStage("build");
      return null;
    }
    const step = activitySteps[activityIdx];
    const value = activity[step.id] || "";
    const canAdvance = value.trim().length > 0;
    return (
      <Card testId="sf-activity">
        <Master current={current} total={TOTAL} />
        <Heading icon={<span className="text-xl">🎯</span>} label="Activity" />
        <Pill
          color="pink"
          text={`Step ${activityIdx + 1} of ${activitySteps.length}`}
          testId="sf-activity-position"
        />
        <h3 className="text-lg font-bold text-gray-900 mt-3 mb-3">{step.prompt}</h3>
        {step.multiline ? (
          <>
            <textarea
              id={`a-${step.id}`}
              value={value}
              onChange={(e) => setActivity((p) => ({ ...p, [step.id]: e.target.value }))}
              placeholder={step.placeholder || ""}
              rows={5}
              className="w-full px-4 py-3 border-2 border-pink-200 rounded-lg focus:outline-none focus:border-pink-500"
              data-testid={`sf-activity-${step.id}`}
            />
            <div className="mt-1.5 flex justify-end">
              <MicButton
                inputId={`a-${step.id}`}
                testId={`sf-activity-mic-${step.id}`}
                size="sm"
                onTranscript={(t) =>
                  setActivity((p) => ({ ...p, [step.id]: ((p[step.id] || "") ? p[step.id] + " " : "") + t.trim() }))
                }
              />
            </div>
          </>
        ) : (
          <input
            type="text"
            value={value}
            onChange={(e) => setActivity((p) => ({ ...p, [step.id]: e.target.value }))}
            placeholder={step.placeholder || ""}
            className="w-full px-4 py-3 border-2 border-pink-200 rounded-lg focus:outline-none focus:border-pink-500"
            data-testid={`sf-activity-${step.id}`}
          />
        )}
        <NavRow
          onBack={activityIdx > 0 ? () => setActivityIdx(activityIdx - 1) : () => setStage(engageQs.length > 0 ? "engage" : "learn")}
          backLabel="Back"
          onNext={() => {
            if (activityIdx < activitySteps.length - 1) setActivityIdx(activityIdx + 1);
            else setStage("build");
          }}
          nextDisabled={!canAdvance}
          nextLabel={activityIdx < activitySteps.length - 1 ? "Next" : "Build"}
          testIdPrefix="sf-activity"
        />
      </Card>
    );
  }

  // ===== BUILD =====
  if (stage === "build") {
    const required = (session.build && session.build.required) || buildFields.map((f) => f.id);
    const ready = required.every((id) => (build[id] || "").trim());
    return (
      <Card testId="sf-build" extra="bg-gradient-to-br from-yellow-50 via-white to-orange-50 border-2 border-yellow-200">
        <Master current={current} total={TOTAL} />
        <Heading icon={<Hammer className="w-5 h-5 text-orange-500" />} label="Build" />
        {session.build?.instructions && (
          <p className="text-gray-700 mb-4">{session.build.instructions}</p>
        )}
        <div className="space-y-4">
          {buildFields.map((f) => (
            <div key={f.id}>
              <label htmlFor={`b-${f.id}`} className="block font-semibold text-gray-800 mb-1">
                {f.label}
                {required.includes(f.id) && <span className="text-orange-600"> *</span>}
              </label>
              {f.type === "textarea" ? (
                <>
                  <textarea
                    id={`b-${f.id}`}
                    value={build[f.id] || ""}
                    onChange={(e) => setBuild((p) => ({ ...p, [f.id]: e.target.value }))}
                    placeholder={f.placeholder || ""}
                    rows={3}
                    className="w-full px-4 py-3 border-2 border-orange-200 rounded-lg focus:outline-none focus:border-orange-500"
                    data-testid={`sf-build-${f.id}`}
                  />
                  <div className="mt-1.5 flex justify-end">
                    <MicButton
                      inputId={`b-${f.id}`}
                      testId={`sf-build-mic-${f.id}`}
                      size="sm"
                      onTranscript={(t) =>
                        setBuild((p) => ({ ...p, [f.id]: ((p[f.id] || "") ? p[f.id] + " " : "") + t.trim() }))
                      }
                    />
                  </div>
                </>
              ) : (
                <input
                  id={`b-${f.id}`}
                  type="text"
                  value={build[f.id] || ""}
                  onChange={(e) => setBuild((p) => ({ ...p, [f.id]: e.target.value }))}
                  placeholder={f.placeholder || ""}
                  className="w-full px-4 py-3 border-2 border-orange-200 rounded-lg focus:outline-none focus:border-orange-500"
                  data-testid={`sf-build-${f.id}`}
                />
              )}
            </div>
          ))}
        </div>
        <NavRow
          onBack={() => setStage(activitySteps.length > 0 ? "activity" : "engage")}
          backLabel="Back"
          onNext={() => setStage("reflect")}
          nextDisabled={!ready}
          nextLabel="Reflect"
          testIdPrefix="sf-build"
        />
      </Card>
    );
  }

  // ===== REFLECT =====
  // reflect stage
  const reflectValue = reflect ? (reflectAnswer[reflect.id] || "") : "ok";
  const reflectReady = reflect ? !!reflectValue : true;
  return (
    <Card testId="sf-reflect" extra="bg-gradient-to-br from-emerald-50 via-white to-teal-50 border-2 border-emerald-200">
      <Master current={current} total={TOTAL} />
      <Heading icon={<MessageSquareHeart className="w-5 h-5 text-emerald-600" />} label="Reflect" />
      {reflect ? (
        <>
          <h3 className="text-lg font-bold text-gray-900 mt-2 mb-3">{reflect.prompt}</h3>
          {reflect.type === "mcq" ? (
            <div className="space-y-3">
              {(reflect.options || []).map((opt) => {
                const sel = reflectValue === opt.value;
                return (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      sel ? "bg-emerald-50 border-emerald-500 shadow-sm" : "bg-white border-gray-200 hover:border-emerald-300"
                    }`}
                    data-testid={`sf-reflect-opt-${opt.value}`}
                  >
                    <input
                      type="radio"
                      name={reflect.id}
                      value={opt.value}
                      checked={sel}
                      onChange={() => setReflectAnswer((p) => ({ ...p, [reflect.id]: opt.value }))}
                      className="mt-1 accent-emerald-500"
                    />
                    <span className="text-gray-800 font-medium">{opt.label}</span>
                  </label>
                );
              })}
            </div>
          ) : (
            <>
              <textarea
                id="sf-reflect-input-field"
                value={reflectValue}
                onChange={(e) => setReflectAnswer((p) => ({ ...p, [reflect.id]: e.target.value }))}
                placeholder={reflect.placeholder || "Type your reflection..."}
                rows={4}
                className="w-full px-4 py-3 border-2 border-emerald-200 rounded-lg focus:outline-none focus:border-emerald-500"
                data-testid="sf-reflect-input"
              />
              <div className="mt-1.5 flex justify-end">
                <MicButton
                  inputId="sf-reflect-input-field"
                  testId="sf-reflect-mic-btn"
                  size="sm"
                  onTranscript={(t) =>
                    setReflectAnswer((p) => ({
                      ...p,
                      [reflect.id]: ((p[reflect.id] || "") ? p[reflect.id] + " " : "") + t.trim(),
                    }))
                  }
                />
              </div>
            </>
          )}
        </>
      ) : (
        <p className="text-gray-700">Great work! Click below to finish this session.</p>
      )}
      <div className="flex justify-between items-center gap-3 mt-6 flex-wrap">
        <button
          onClick={() => setStage("build")}
          className="flex items-center gap-1 px-4 py-2 bg-white border-2 border-emerald-300 text-emerald-700 font-semibold rounded-lg hover:bg-emerald-50"
          data-testid="sf-reflect-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={() =>
            onReadyToSubmit?.({
              engage,
              activity,
              build,
              reflect: reflectAnswer,
            })
          }
          disabled={!reflectReady || submitting}
          className="flex items-center gap-1 px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
          data-testid="sf-reflect-submit-btn"
        >
          {submitting ? "Saving..." : "Finish Session 🎉"}
          {!submitting && <CheckCircle2 className="w-5 h-5" />}
        </button>
      </div>
    </Card>
  );
}

// ============== Helpers (kept inline; small) ==============

function Master({ current, total }) {
  const pct = Math.round((current / total) * 100);
  return (
    <div className="mb-5" data-testid="sf-master-progress">
      <div className="flex justify-between text-xs font-semibold text-gray-500 mb-1">
        <span>Step {current} of {total}</span>
        <span>{pct}%</span>
      </div>
      <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-emerald-500 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function Heading({ icon, label }) {
  return (
    <div className="flex items-center gap-2 mb-1">
      {icon}
      <h2 className="text-xl font-bold text-gray-800">{label}</h2>
    </div>
  );
}

function Pill({ color, text, testId }) {
  const map = {
    purple: "text-purple-600 bg-purple-50 border-purple-200",
    pink: "text-pink-600 bg-pink-50 border-pink-200",
  };
  return (
    <span
      className={`text-xs font-bold border px-2 py-0.5 rounded-full ${map[color] || map.purple}`}
      data-testid={testId}
    >
      {text}
    </span>
  );
}

function Card({ children, testId, extra = "" }) {
  return (
    <div
      className={`rounded-2xl shadow-lg p-6 sm:p-8 ${extra || "bg-white"}`}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

function NavRow({ onBack, backLabel, onNext, nextDisabled, nextLabel, testIdPrefix }) {
  return (
    <div className="flex justify-between items-center gap-3 mt-6 flex-wrap">
      <button
        onClick={onBack}
        className="flex items-center gap-1 px-4 py-2 bg-white border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50"
        data-testid={`${testIdPrefix}-back-btn`}
      >
        <ArrowLeft className="w-4 h-4" /> {backLabel}
      </button>
      <button
        onClick={onNext}
        disabled={nextDisabled}
        className="flex items-center gap-1 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-xl shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid={`${testIdPrefix}-next-btn`}
      >
        {nextLabel} <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function NextRow({ onNext, label, testId }) {
  return (
    <div className="flex justify-end mt-6">
      <button
        onClick={onNext}
        className="flex items-center gap-1 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-xl shadow-md hover:shadow-lg"
        data-testid={testId}
      >
        {label} <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

export default SessionFlow;
